import { z } from "zod";

import { LeadLabelType, leadLabelsSchema } from "@signalist/shared-types";

import { ListSourceType } from "@/enum/list.enum";

import { ContactProspect, ContactProspectOrganization } from "./contacts";
import { People, ProspectEmployments } from "../../Apollo/schema/get-people";

export interface ContactProspectColumnValueInfo {
	id: number;
	name: string;
	type: string | null;
	value: unknown;
	sourceUrl?: string | null;
	answerExplanation?: string | null;
}

export interface ContactCrmDealInfo {
	id: number;
	prospectId: number | null;
	crmType: string | null;
	referenceId: string | null;
	url: string | null;
	dealName: string | null;
	dealStage: string | null;
	pipeline: string | null;
	dealOwner: string | null;
	dealOwnerId: string | null;
	syncStatus: string | null;
	syncError?: string | null;
	lastUpdatedAt: string | null;
	createdAt: string | null;
	updatedAt: string | null;
}

export interface ContactProspectSourceContext {
	listPersonId: number;
	listId: number;
	listName: string | null;
	sourceType: ListSourceType | string | null;
	sourceName: string | null;
	customColumns?: unknown;
	additionalInformation?: unknown;
	liPostResult?: unknown | null;
	liEventEntryAttendee?: unknown | null;
	liEventEntry?: unknown | null;
	liCompanyActivityEntryResult?: unknown | null;
	offer?: unknown | null;
	mapBusiness?: unknown | null;
	columns?: ContactProspectColumnValueInfo[];
}

export interface ContactProspectDetailOrganization extends ContactProspectOrganization {
	employeesFrom?: number | null;
	employeesTo?: number | null;
	companyLocations?: Array<{
		city: string | null;
		state: string | null;
		country: string | null;
	}> | null;
}

export interface ContactProspectDetail extends ContactProspect {
	organization: ContactProspectDetailOrganization | null;
	about?: string | null;
	jobChangeOrPromotion?: string | null;
	startedCurrentPosition?: string | null;
	/** Timestamp of the prospect's most recent enrichment (LinkedIn refetch); null when never enriched. */
	lastEnrichedAt?: string | null;
	prospectEmployments?: ProspectEmployments[] | null;
	deepResearchResponse?: People["deepResearchResponse"];
	crmDeals?: ContactCrmDealInfo[];
	sourceContexts?: ContactProspectSourceContext[];
	/**
	 * Prospect-wide lead "color label" state, priority-ordered (first entry = dominant state).
	 * Drives the Chrome extension's injected LinkedIn profile badge.
	 */
	labels?: LeadLabelType[];
}

export const ContactProspectSourceContextSchema = z
	.object({
		listPersonId: z.number(),
		listId: z.number(),
		listName: z.string().nullable().optional(),
		sourceType: z.string().nullable().optional(),
		sourceName: z.string().nullable().optional(),
		columns: z.array(z.unknown()).optional(),
	})
	.passthrough();

export const ContactProspectDetailSchema = z
	.object({
		id: z.number(),
		about: z.string().nullable().optional(),
		jobChangeOrPromotion: z.string().nullable().optional(),
		startedCurrentPosition: z.string().nullable().optional(),
		lastEnrichedAt: z.string().nullable().optional(),
		prospectEmployments: z.array(z.unknown()).nullable().optional(),
		deepResearchResponse: z.unknown().nullable().optional(),
		crmDeals: z.array(z.unknown()).optional(),
		sourceContexts: z.array(ContactProspectSourceContextSchema).optional(),
		labels: leadLabelsSchema.optional(),
	})
	.passthrough() as unknown as z.ZodType<ContactProspectDetail>;
